/* ================================================================
   LAIB Sections JS v2
   assets/laib-sections.js
   ================================================================ */
'use strict';

/* ── Scroll reveal ──────────────────────────────────────────── */
(function initReveal() {
  if (!('IntersectionObserver' in window)) {
    document.querySelectorAll('[data-reveal]').forEach(el => el.classList.add('is-visible'));
    return;
  }
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
  document.querySelectorAll('[data-reveal]').forEach(el => io.observe(el));
})();

/* ── Hero carousel ──────────────────────────────────────────── */
class LaibHero {
  constructor(root) {
    this.root    = root;
    this.track   = root.querySelector('.laib-hero__slides');
    this.slides  = [...root.querySelectorAll('.laib-hero__slide')];
    this.dots    = [...root.querySelectorAll('.laib-hero__dot')];
    this.idx     = 0;
    this.timer   = null;
    this.speed   = (parseInt(root.dataset.speed) || 5) * 1000;

    /* Expose speed to CSS for the progress-dot animation */
    root.style.setProperty('--hero-speed', `${this.speed / 1000}s`);

    /* Always ensure the first slide's video plays */
    this._playVideo(this.slides[0]);

    /* Activate first slide immediately so CSS animations fire */
    this._activateSlide(this.slides[0]);

    if (this.slides.length < 2) return;
    this._bind();
    this._autoplay();
    this._render();
  }

  _playVideo(slide) {
    const v = slide?.querySelector('.laib-hero__video');
    if (!v) return;
    v.currentTime = 0;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }

  _pauseVideo(slide) {
    slide?.querySelector('.laib-hero__video')?.pause();
  }

  _activateSlide(slide) {
    if (!slide) return;
    /* Reset content animations so they replay on each slide visit */
    const els = slide.querySelectorAll(
      '.laib-hero__eyebrow, .laib-hero__heading, .laib-hero__subtext, .laib-hero__ctas'
    );
    slide.classList.remove('is-active');
    els.forEach(el => { el.style.animation = 'none'; });
    void slide.offsetWidth; /* single reflow */
    els.forEach(el => { el.style.animation = ''; });
    slide.classList.add('is-active');
  }

  _bind() {
    this.root.querySelector('.laib-hero__arrow--prev')
      ?.addEventListener('click', () => this._step(-1));
    this.root.querySelector('.laib-hero__arrow--next')
      ?.addEventListener('click', () => this._step(1));

    this.dots.forEach((d, i) => d.addEventListener('click', () => this._go(i)));

    /* Touch */
    let x0 = 0;
    this.root.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; }, { passive: true });
    this.root.addEventListener('touchend',   e => {
      const dx = x0 - e.changedTouches[0].clientX;
      if (Math.abs(dx) > 50) this._step(dx > 0 ? 1 : -1);
    }, { passive: true });

    /* Keyboard */
    this.root.setAttribute('tabindex', '0');
    this.root.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  this._step(-1);
      if (e.key === 'ArrowRight') this._step(1);
    });

    /* Pause on hover/focus */
    this.root.addEventListener('mouseenter', () => this._pause());
    this.root.addEventListener('mouseleave', () => this._autoplay());
  }

  _step(dir) { this._go(this.idx + dir); }

  _go(i) {
    const prev = this.idx;
    const n    = this.slides.length;
    this.idx   = ((i % n) + n) % n;
    this.slides[prev]?.classList.remove('is-active');
    this._pauseVideo(this.slides[prev]);
    this._render();
    this._playVideo(this.slides[this.idx]);
    this._activateSlide(this.slides[this.idx]);
    this._restart();
  }

  _render() {
    this.track.style.transform = `translateX(-${this.idx * 100}%)`;
    this.dots.forEach((d, i) => {
      const active = i === this.idx;
      d.classList.toggle('is-active', active);
      d.setAttribute('aria-selected', String(active));

      /* nothing extra needed — is-active CSS handles the dot style */
    });
  }

  _autoplay() {
    if (this.timer) return;
    this.timer = setInterval(() => this._step(1), this.speed);
  }
  _pause()   { clearInterval(this.timer); this.timer = null; }
  _restart() { this._pause(); this._autoplay(); }
}
document.querySelectorAll('.laib-hero').forEach(el => new LaibHero(el));

/* ── Nav drawer ─────────────────────────────────────────────── */
class LaibDrawer {
  constructor() {
    this.toggle  = document.querySelector('.laib-hdr__hamburger');
    this.drawer  = document.querySelector('.laib-drawer');
    this.closeBtn= document.querySelector('.laib-drawer__close');
    this.overlay = document.querySelector('.laib-drawer__overlay');
    if (!this.drawer) return;
    this._bind();
  }
  _bind() {
    this.toggle?.addEventListener('click',   () => this._open());
    this.closeBtn?.addEventListener('click', () => this._close());
    this.overlay?.addEventListener('click',  () => this._close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.drawer.classList.contains('is-open')) this._close();
    });
  }
  _open()  {
    this.drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.closeBtn?.focus();
    this.toggle?.setAttribute('aria-expanded', 'true');
  }
  _close() {
    this.drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.toggle?.focus();
  }
}
new LaibDrawer();

/* ── Drag-scroll (TikTok, any .laib-ttk__scroll) ───────────── */
document.querySelectorAll('.laib-ttk__scroll, .laib-pgrid').forEach(wrap => {
  let down = false, sx = 0, sl = 0, moved = false;

  wrap.addEventListener('mousedown', e => {
    down = true; moved = false;
    wrap.classList.add('is-grabbing');
    sx = e.pageX - wrap.offsetLeft;
    sl = wrap.scrollLeft;
  });
  document.addEventListener('mouseup', () => { down = false; wrap.classList.remove('is-grabbing'); });
  wrap.addEventListener('mousemove', e => {
    if (!down) return;
    moved = true;
    e.preventDefault();
    wrap.scrollLeft = sl - (e.pageX - wrap.offsetLeft - sx) * 1.6;
  });
  /* Cancel link click if user dragged */
  wrap.querySelectorAll('a').forEach(a =>
    a.addEventListener('click', e => { if (moved) { e.preventDefault(); moved = false; } })
  );
});

/* ── Newsletter AJAX ────────────────────────────────────────── */
document.querySelectorAll('.laib-nl-form').forEach(form => {
  const input   = form.querySelector('.laib-nl__input');
  const ok      = form.closest('.laib-nl__body')?.querySelector('.laib-nl__ok');
  const btn     = form.querySelector('.laib-nl__submit');

  form.addEventListener('submit', async e => {
    e.preventDefault();
    if (!input?.value.trim()) return;
    if (btn) { btn.textContent = '…'; btn.disabled = true; }

    try {
      const body = new FormData(form);
      const res  = await fetch(form.action, {
        method: 'POST',
        body,
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (res.ok) {
        ok?.classList.add('is-on');
        input.value = '';
        setTimeout(() => ok?.classList.remove('is-on'), 6000);
      } else { form.submit(); }
    } catch { form.submit(); }
    finally {
      if (btn) { btn.textContent = 'Join now'; btn.disabled = false; }
    }
  });
});

/* ── Identity tiles prev/next arrows ───────────────────────── */
document.querySelectorAll('.laib-ident__carousel').forEach(carousel => {
  const outer = carousel.querySelector('.laib-ident__outer');
  const prev  = carousel.querySelector('.laib-ident__arrow--prev');
  const next  = carousel.querySelector('.laib-ident__arrow--next');
  if (!outer || !prev || !next) return;

  const tileWidth = () => {
    const tile = outer.querySelector('.laib-ident__tile');
    const gap  = parseInt(getComputedStyle(outer.querySelector('.laib-ident__grid')).gap) || 16;
    return tile ? tile.offsetWidth + gap : 220;
  };

  const updateArrows = () => {
    prev.disabled = outer.scrollLeft <= 2;
    next.disabled = outer.scrollLeft >= outer.scrollWidth - outer.clientWidth - 2;
  };

  prev.addEventListener('click', () => { outer.scrollBy({ left: -tileWidth(), behavior: 'smooth' }); });
  next.addEventListener('click', () => { outer.scrollBy({ left:  tileWidth(), behavior: 'smooth' }); });
  outer.addEventListener('scroll', updateArrows, { passive: true });
  updateArrows();
});

/* ── Transparent native header over hero ────────────────────── */
(function initHeroHeader() {
  const hdr  = document.querySelector('.section-header');
  const hero = document.querySelector('.laib-hero');
  if (!hdr || !hero) return;
  hdr.classList.add('laib-has-hero');
  const tick = () => {
    /* Go opaque once the hero bottom edge rises above the header bottom */
    const heroBottom = hero.getBoundingClientRect().bottom;
    hdr.classList.toggle('laib-hdr-opaque', heroBottom < hdr.offsetHeight + 20);
  };
  window.addEventListener('scroll', tick, { passive: true });
  window.addEventListener('resize', tick, { passive: true });
  tick();
})();

/* ── Menu drawer: close button + submenu accordion ───────────── */
(function initMenuDrawer() {
  const details = document.querySelector('#Details-menu-drawer-container');
  const panel   = document.querySelector('#menu-drawer');
  if (!details || !panel) return;

  const summary = details.querySelector('summary');

  /* Must stay in step with the transition on .menu-drawer__submenu */
  const ANIM_MS = 320;

  /*
   * Start the drawer where the HEADER starts, i.e. just under the announcement
   * bar.
   *
   * We anchor to the header's top rather than the announcement bar's bottom.
   * Those are the same edge when nothing has scrolled, but the header is
   * sticky and the bar is not — so once you scroll, the header's top is
   * pinned at 0 while the bar's bottom goes negative. Measuring the header
   * therefore self-corrects in both states, and avoids any gap from margins or
   * borders sitting between the two sections.
   *
   * It must be live, not measured once: a fixed offset would leave a strip of
   * page content showing above the drawer as soon as you scrolled.
   */
  const annBar = document.querySelector('.announcement-bar-section');

  if (annBar) {
    const syncDrawerTop = () => {
      /*
       * offsetHeight - scrollY, NOT getBoundingClientRect().
       *
       * The header is position:sticky and the announcement bar is not, so a
       * viewport-relative rect on either is affected by the sticky offset and
       * came back as the header's BOTTOM rather than its top — which parked
       * the drawer one header-height too low. The bar's own offsetHeight is a
       * static layout value that no sticky behaviour can perturb; subtracting
       * scrollY gives its live on-screen bottom edge, and clamping at 0 makes
       * the drawer go full-height once the bar has scrolled away.
       */
      const top = Math.max(0, annBar.offsetHeight - window.scrollY);
      document.documentElement.style.setProperty('--laib-drawer-top', `${top}px`);
    };
    syncDrawerTop();
    window.addEventListener('scroll', syncDrawerTop, { passive: true });
    window.addEventListener('resize', syncDrawerTop, { passive: true });
    /* Re-measure on open, in case the bar's height changed (e.g. a rotated
     * phone rewrapping a long announcement onto two lines). */
    summary?.addEventListener('click', () => requestAnimationFrame(syncDrawerTop));
  }

  /*
   * Close button. We deliberately click the <summary> rather than setting
   * details.open = false: Dawn's HeaderDrawer.closeMenuDrawer() is what
   * releases the body scroll lock (overflow-hidden-*) and the focus trap.
   * Bypassing it leaves the page unscrollable.
   */
  panel.querySelector('.laib-drawer-top__close')
    ?.addEventListener('click', () => summary?.click());

  const expand = (el) => {
    el.setAttribute('open', '');
    el.querySelector('summary')?.setAttribute('aria-expanded', 'true');
    /* Two frames: the browser must paint the 0fr start state before we
     * switch to 1fr, or there is nothing to interpolate from. */
    requestAnimationFrame(() => requestAnimationFrame(() => el.classList.add('menu-opening')));
  };

  const collapse = (el) => {
    el.classList.remove('menu-opening');
    el.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    /* Keep `open` until the collapse finishes — dropping it immediately makes
     * the browser hide the content and there is nothing left to animate. */
    setTimeout(() => {
      if (!el.classList.contains('menu-opening')) el.removeAttribute('open');
    }, ANIM_MS);
  };

  /*
   * Dawn's MenuDrawer.onSummaryClick has no close branch for submenus: it only
   * ever ADDS .menu-opening, because in its design you back out of a submenu
   * via the close button (which an accordion has no use for, so we hide it).
   * Re-clicking an open summary therefore let native <details> snap shut with
   * no animation, and left aria-expanded="true" on a closed panel.
   *
   * Dawn binds its handler on each <summary> itself, so a capture-phase
   * listener up here runs first and stopImmediatePropagation keeps the event
   * from ever reaching it. We own the toggle from here on.
   */
  panel.addEventListener('click', (event) => {
    const clicked = event.target.closest('summary.menu-drawer__menu-item');
    if (!clicked || !panel.contains(clicked)) return;

    const item = clicked.parentElement;
    if (!item || item.tagName !== 'DETAILS') return;

    event.preventDefault();
    event.stopImmediatePropagation();

    if (item.classList.contains('menu-opening')) {
      collapse(item);
      return;
    }

    /* Accordion: only one submenu open per level */
    item.parentElement?.parentElement
      ?.querySelectorAll(':scope > li > details.menu-opening')
      .forEach((sibling) => { if (sibling !== item) collapse(sibling); });

    expand(item);
  }, true);
})();

/* ── Desktop nav: open dropdowns on hover ───────────────────── */
(function initHeaderHoverMenus() {
  const nav = document.querySelector('.section-header .header__inline-menu');
  if (!nav) return;

  /* Hover-open only makes sense with a real pointer and only at the width
   * where the inline nav actually exists — below 990px it's the drawer. */
  const canHover = window.matchMedia('(hover: hover) and (min-width: 990px)');

  nav.querySelectorAll('header-menu > details').forEach((details) => {
    const item    = details.closest('li');
    const trigger = details.querySelector('summary');
    if (!item || !trigger) return;

    let closeTimer;

    const open = () => {
      clearTimeout(closeTimer);
      details.setAttribute('open', '');
      trigger.setAttribute('aria-expanded', 'true');
    };

    /* Small grace period: without it, clipping a corner on the way down to
     * the panel snaps it shut. */
    const close = () => {
      clearTimeout(closeTimer);
      closeTimer = setTimeout(() => {
        details.removeAttribute('open');
        trigger.setAttribute('aria-expanded', 'false');
      }, 140);
    };

    /* Bound to the <li>, which contains the summary AND the panel, so moving
     * between them never counts as leaving. */
    item.addEventListener('mouseenter', () => canHover.matches && open());
    item.addEventListener('mouseleave', () => canHover.matches && close());
  });
})();

/* ── FAQ Accordion ──────────────────────────────────────────── */
document.querySelectorAll('.laib-faq__q').forEach(btn => {
  btn.addEventListener('click', () => {
    const item   = btn.closest('.laib-faq__item');
    const isOpen = item.classList.contains('is-open');
    document.querySelectorAll('.laib-faq__item.is-open').forEach(el => el.classList.remove('is-open'));
    btn.setAttribute('aria-expanded', 'false');
    if (!isOpen) {
      item.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    }
  });
});

/* ── Sticky add-to-cart (standard PDP, mobile) ───────────────── */
(function initStickyAtc() {
  const bar = document.querySelector('[data-laib-sticky-atc]');
  if (!bar) return;

  /* The REAL add-to-cart button inside <product-form>. We delegate to it rather
   * than posting our own form, so quantity, variant, personalisation inputs and
   * Dawn's validation all apply. A duplicate form would drift out of sync. */
  const realForm = document.querySelector('product-form');
  const realBtn  = realForm?.querySelector('.product-form__submit');
  if (!realBtn) return;

  bar.querySelector('[data-laib-satc-add]')
    ?.addEventListener('click', () => realBtn.click());

  /* Show the bar only once the real button has scrolled out of view — showing
   * it while the CTA is already on screen just covers content for no gain. */
  const io = new IntersectionObserver(
    ([entry]) => {
      const show = !entry.isIntersecting;
      bar.classList.toggle('is-visible', show);
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    },
    { rootMargin: '0px 0px -40px 0px' }
  );
  io.observe(realBtn);

  /* Dawn re-renders the product form on variant change, which swaps the button
   * and the price. Mirror both so the sticky bar can't show a stale price or a
   * live button for a sold-out variant. */
  const priceEl = bar.querySelector('[data-laib-satc-price]');
  const stickyBtn = bar.querySelector('[data-laib-satc-add]');

  new MutationObserver(() => {
    const btn = document.querySelector('product-form .product-form__submit');
    if (!btn) return;
    stickyBtn.disabled = btn.disabled;
    stickyBtn.textContent = btn.disabled
      ? btn.textContent.trim()
      : btn.querySelector('span')?.textContent.trim() || btn.textContent.trim();

    const livePrice = document.querySelector('.price__container .price-item--regular, .price__container .price-item--sale');
    if (livePrice && priceEl) priceEl.textContent = livePrice.textContent.trim();
  }).observe(realForm, { childList: true, subtree: true, attributes: true });
})();
